import React, { useEffect, useState } from 'react';
import {
  TweetSettingsValues,
  clampHumanizeLevel,
  defaultTweetSettings,
  loadTweetSettingsFromStorage,
  saveTweetSettingsToStorage,
} from '../lib/tweetSettings';
import { useT } from '../lib/i18n';
import { cn } from '../lib/cn';

interface TweetSettingsProps {
  value?: TweetSettingsValues;
  onChange?: (settings: TweetSettingsValues) => void;
  className?: string;
  title?: string;
  description?: string;
}

export const TweetSettings: React.FC<TweetSettingsProps> = ({
  value,
  onChange,
  className,
  title,
  description,
}) => {
  const { t } = useT();
  const displayTitle = title || t('tweetSettings.title');
  const displayDesc = description || t('tweetSettings.description');

  const [settings, setSettings] = useState<TweetSettingsValues>(value || loadTweetSettingsFromStorage());

  useEffect(() => {
    if (value) setSettings(value);
  }, [value]);

  useEffect(() => {
    saveTweetSettingsToStorage(settings);
    onChange?.(settings);
  }, [settings, onChange]);

  const updateSettings = (partial: Partial<TweetSettingsValues>) => {
    setSettings((prev) => ({
      ...prev,
      ...partial,
      humanizeLevel: clampHumanizeLevel(partial.humanizeLevel ?? prev.humanizeLevel),
    }));
  };

  const lengthOptions = [
    { value: 'short' as const, label: t('tweetSettings.length.short'), helper: t('tweetSettings.length.shortHelper') },
    { value: 'medium' as const, label: t('tweetSettings.length.medium'), helper: t('tweetSettings.length.mediumHelper') },
    { value: 'long' as const, label: t('tweetSettings.length.long'), helper: t('tweetSettings.length.longHelper') },
  ];

  const toneOptions = [
    { value: 'one_liner' as const, label: t('tweetSettings.tone.oneLiner'), helper: t('tweetSettings.tone.oneLinerHelper') },
    { value: 'explanatory' as const, label: t('tweetSettings.tone.explanatory'), helper: t('tweetSettings.tone.explanatoryHelper') },
    { value: 'story' as const, label: t('tweetSettings.tone.story'), helper: t('tweetSettings.tone.storyHelper') },
  ];

  const styleOptions = [
    { value: 'assertive' as const, label: t('tweetSettings.style.assertive'), helper: t('tweetSettings.style.assertiveHelper') },
    { value: 'empathetic' as const, label: t('tweetSettings.style.empathetic'), helper: t('tweetSettings.style.empatheticHelper') },
    { value: 'warning' as const, label: t('tweetSettings.style.warning'), helper: t('tweetSettings.style.warningHelper') },
    { value: 'secret' as const, label: t('tweetSettings.style.secret'), helper: t('tweetSettings.style.secretHelper') },
  ];

  return (
    <div className={cn('bg-surface-2 border border-border rounded-xl p-4 sm:p-5 space-y-4', className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base sm:text-lg font-bold text-brand">{displayTitle}</h3>
          <p className="text-xs sm:text-sm text-text-muted mt-1">{displayDesc}</p>
        </div>
        <button
          type="button"
          onClick={() => setSettings(defaultTweetSettings)}
          className="text-xs text-text-muted hover:text-brand transition-colors"
        >
          ↺ {t('tweetSettings.reset')}
        </button>
      </div>

      {/* Length */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-text-primary">{t('tweetSettings.lengthLabel')}</label>
          <span className="text-xs text-text-muted">{t('tweetSettings.lengthHint')}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {lengthOptions.map((option) => {
            const active = settings.length === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => updateSettings({ length: option.value })}
                className={cn(
                  'p-3 rounded-lg border text-left transition-all',
                  active
                    ? 'border-brand bg-brand/10 text-brand shadow-[0_0_0_1px_rgba(59,130,246,0.3)]'
                    : 'border-border bg-bg text-text-primary hover:border-brand/60 hover:bg-brand/5'
                )}
              >
                <div className="text-sm font-semibold">{option.label}</div>
                <div className="text-xs text-text-muted mt-1">{option.helper}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tone & Style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-semibold text-text-primary">{t('tweetSettings.toneLabel')}</label>
          <div className="mt-2 space-y-2">
            {toneOptions.map((option) => {
              const active = settings.tone === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateSettings({ tone: option.value })}
                  className={cn(
                    'w-full p-3 rounded-lg border text-left transition-all',
                    active
                      ? 'border-brand bg-brand/10 text-brand shadow-[0_0_0_1px_rgba(59,130,246,0.3)]'
                      : 'border-border bg-bg text-text-primary hover:border-brand/60 hover:bg-brand/5'
                  )}
                >
                  <div className="text-sm font-semibold">{option.label}</div>
                  <div className="text-xs text-text-muted mt-1">{option.helper}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold text-text-primary">{t('tweetSettings.styleLabel')}</label>
          <div className="mt-2 space-y-2">
            {styleOptions.map((option) => {
              const active = settings.style === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateSettings({ style: option.value })}
                  className={cn(
                    'w-full p-3 rounded-lg border text-left transition-all',
                    active
                      ? 'border-brand bg-brand/10 text-brand shadow-[0_0_0_1px_rgba(59,130,246,0.3)]'
                      : 'border-border bg-bg text-text-primary hover:border-brand/60 hover:bg-brand/5'
                  )}
                >
                  <div className="text-sm font-semibold">{option.label}</div>
                  <div className="text-xs text-text-muted mt-1">{option.helper}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Humanize Level */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-text-primary">{t('tweetSettings.humanizeLabel')}</label>
          <span className="text-xs text-brand font-bold">Lv.{settings.humanizeLevel}</span>
        </div>
        <input
          type="range"
          min={1}
          max={5}
          step={1}
          value={settings.humanizeLevel}
          onChange={(e) => updateSettings({ humanizeLevel: Number(e.target.value) })}
          className="w-full accent-brand"
        />
        <p className="text-xs text-text-muted mt-1">
          {t('tweetSettings.humanizeDesc')}
        </p>
      </div>
    </div>
  );
};
